import { CellContext, HeaderContext } from '@tanstack/react-table';
import { Checkbox, Skeleton, tableHelpers } from '@round/creatorbase-ui';
import { getMeta, OutreachResultRow } from '../../../../helpers';
import styles from './SelectCell.module.css';

const SELECT_ALL_LABEL = 'Select all loaded creators';

type TableRow = tableHelpers.WithLoadingRow<OutreachResultRow>;

export const SelectResultCell = ({ row: { original }, table }: CellContext<TableRow, unknown>) => {
    const { isLoading, selectedIds, onToggleResult } = getMeta(table);

    if (tableHelpers.isLoadingRow(original) || isLoading) {
        return <Skeleton className={styles.skeleton} />;
    }

    return (
        // Checkbox renders as a `role="checkbox"` span, not a real button or link, so the row's click
        // guard doesn't already exclude it. It also answers a click by re-dispatching a second, bubbling
        // click at a hidden input beside it, which is a separate DOM event the guard cannot catch either
        // — so both are stopped one level up, at a wrapper that does not itself take part in the cell's
        // layout.
        <div className={styles.clickGuard} onClick={(e) => e.stopPropagation()}>
            <Checkbox
                checked={selectedIds.has(original.id)}
                onCheckedChange={() => onToggleResult(original.id)}
                aria-label={`Select @${original.username}`}
            />
        </div>
    );
};

/** Takes the loaded rows, which are the only results a batch has ids for. */
export const SelectAllResultsHeader = ({ table }: HeaderContext<TableRow, unknown>) => {
    const { selectedIds, onSelectResults, onClearSelection } = getMeta(table);

    // A loading row carries no id, so it never counts towards a selection.
    const selectableIds = table
        .getRowModel()
        .rows.map(({ original }) => (tableHelpers.isLoadingRow(original) ? undefined : original.id))
        .filter((id): id is number => typeof id === 'number');

    const areAllSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
    const areSomeSelected = selectableIds.some((id) => selectedIds.has(id));

    return (
        <Checkbox
            checked={areAllSelected}
            indeterminate={!areAllSelected && areSomeSelected}
            onCheckedChange={() => (areAllSelected ? onClearSelection() : onSelectResults(selectableIds))}
            disabled={!selectableIds.length}
            aria-label={SELECT_ALL_LABEL}
        />
    );
};
